import React, { PropTypes, Component } from "react";
import ReactDOM  from "react-dom";

import d3 from "d3";
import ReStock from "react-stockcharts";

import PureComponent from "utils/PureComponent";
import * as ChartTypes from "constants/ChartTypes";

var { BackgroundText } = ReStock;

var { ChartCanvas, Chart, DataSeries, EventCapture } = ReStock;

var { CandlestickSeries, HistogramSeries, LineSeries, AreaSeries } = ReStock.series;
var { KagiSeries, RenkoSeries, PointAndFigureSeries } = ReStock.series;
var { BollingerSeries, MACDSeries, RSISeries, StochasticSeries } = ReStock.series;

var { MouseCoordinates } = ReStock.coordinates;

var { TooltipContainer, OHLCTooltip, MovingAverageTooltip, BollingerBandTooltip, MACDTooltip, RSITooltip, StochasticTooltip } = ReStock.tooltip;
var { StockscaleTransformer, HeikinAshiTransformer, KagiTransformer, PointAndFigureTransformer, RenkoTransformer } = ReStock.transforms;

var { XAxis, YAxis } = ReStock.axes;

var { MACD, EMA, SMA, BollingerBand, RSI, FullStochasticOscillator } = ReStock.indicator;
var { FibonacciRetracement, TrendLine } = ReStock.interactive;

var margin = {left: 70, right: 70, top:10, bottom: 30};
var dateFormat = d3.time.format("%Y-%m-%d");

function getXAxis(show, verticalGrid) {
	return show
		? <XAxis axisAt="bottom" orient="bottom" {...verticalGrid} tickStroke="#FFFFFF" stroke="#FFFFFF" />
		: <XAxis axisAt="bottom" orient="bottom" showTicks={false} outerTickSize={0} stroke="#FFFFFF" opacity={0.5}/>;
}

function getMainChartType(type) {
	switch (type.value) {
		case ChartTypes.AREA.value: {
			return AreaSeries;
		}
		case ChartTypes.LINE.value: {
			return LineSeries;
		}
		case ChartTypes.CANDLESTICK.value: {
			return CandlestickSeries;
		}
		case ChartTypes.HEIKIN_ASHI.value: {
			return CandlestickSeries;
		}
		case ChartTypes.RENKO.value: {
			return RenkoSeries;
		}
		case ChartTypes.KAGI.value: {
			return KagiSeries;
		}
		case ChartTypes.POINT_FIGURE.value: {
			return PointAndFigureSeries;
		}
	}
}
function getMainChartTypeProps(type) {
	switch (type.value) {
		case ChartTypes.AREA.value: {
			return {};
		}
		case ChartTypes.LINE.value: {
			return {};
		}
		case ChartTypes.CANDLESTICK.value: {
			return { wickStroke: { up: "#6BA583", down: "#db0000" }, fill: { up: "#6BA583", down: "#db0000" } };
		}
		case ChartTypes.HEIKIN_ASHI.value: {
			return { wickStroke: { up: "#6BA583", down: "#db0000" }, fill: { up: "#6BA583", down: "#db0000" } };
		}
		case ChartTypes.RENKO.value: {
			return {};
		}
		case ChartTypes.KAGI.value: {
			return {};
		}
		case ChartTypes.POINT_FIGURE.value: {
			return {};
		}
	}
}

var BOLLINGER_SERIES_PROPS = { fill: "#adffaf", opacity: 0.1, stroke: { top: "brown", middle: "#8c9900", bottom: "brown" } };
var STOCHASTIC_SERIES_PROPS = { stroke: { top: "#37a600", middle: "#b8ab00", bottom: "#37a600" } };
var MACD_SERIES_PROPS = { zeroLineStroke: "#FFFFFF", opacity: 0.4 };
var RSI_SERIES_PROPS = { stroke: { line: "#9E7523", top: "brown", middle: "#27821D", bottom: "brown" } };
var TRENDLINE_PROPS = { currentPositionStroke: "#FFFFFF", stroke: "#FFFF7F", opacity: 0.8 };
var FIB_RETRACEMENT_PROPS = { stroke: "#FFFF7F", fontStroke: "#FFFFFF", opacity: 0.7 };

function getConfig(each, dy, movingAverageTooltipExists, chartHeight, verticalGrid, logger) {
	var chart, dataSeries, toolTip, toolTipDY, indicator;
	var { chartId, dataSeriesId, options } = each;
	// console.log(options);
	switch (each.type) {
		case "BollingerBand": {
			dataSeries = <DataSeries key={dataSeriesId} id={dataSeriesId} indicator={BollingerBand} options={options}>
				<BollingerSeries {...BOLLINGER_SERIES_PROPS}/>
			</DataSeries>;
			var start = dy + 20;
			toolTipDY = start + 5;
			toolTip = <BollingerBandTooltip forChart={chartId} forDataSeries={dataSeriesId} key={`${chartId}-${dataSeriesId}`}
				onClick={logger.bind(null, { chartId, dataSeriesId }, options)} origin={[-48, start]} />;
			break;
		}
		case "SMA": {
			indicator = SMA;
		}
		case "EMA": {
			// console.log(options);
			if (!indicator) indicator = EMA;
			dataSeries = <DataSeries key={dataSeriesId} id={dataSeriesId} indicator={indicator} options={options} >
				<LineSeries/>
			</DataSeries>;
			if (!movingAverageTooltipExists) {
				var start = dy + 5;
				toolTipDY = start + 25;
				toolTip = <MovingAverageTooltip forChart={chartId} key={`${chartId}-${dataSeriesId}`}
					onClick={logger.bind(null, {})} origin={[-45, start]} />;
			}
			break;
		}
		case "MACD": {
			chart = (showXAxis, dataSeries, noOfChartsAfter = 0) => <Chart id={chartId} key={chartId} yMousePointerDisplayLocation="right" yMousePointerDisplayFormat={(y) => y.toFixed(2)}
						height={chartHeight} origin={(w, h) => [0, h - chartHeight - chartHeight * noOfChartsAfter]} padding={{ top: 5, right: 0, bottom: 5, left: 0 }} >
				{ getXAxis(showXAxis, verticalGrid) }
				<YAxis axisAt="right" orient="right" ticks={2} tickStroke="#FFFFFF" stroke="#FFFFFF" />
				<DataSeries id={dataSeriesId} indicator={MACD} options={options} >
					<MACDSeries histogramStroke {...MACD_SERIES_PROPS} />
				</DataSeries>
			</Chart>;
			toolTip = <MACDTooltip ref="MACDTooltip" forChart={chartId} forDataSeries={dataSeriesId} key={`${chartId}-${dataSeriesId}`}
				onClick={logger.bind(null, { chartId, dataSeriesId }, options)} origin={[-48, 15]}/>;
			toolTipDY = dy;
			break;
		}
		case "RSI": {
			chart = (showXAxis, dataSeries, noOfChartsAfter = 0) => <Chart id={chartId} key={chartId} yMousePointerDisplayLocation="right" yMousePointerDisplayFormat={(y) => y.toFixed(2)}
						height={chartHeight} origin={(w, h) => [0, h - chartHeight - chartHeight * noOfChartsAfter]} padding={{ top: 0, right: 0, bottom: 0, left: 0 }} >
				{ getXAxis(showXAxis, verticalGrid) }
				<YAxis axisAt="right" orient="right" ticks={2} tickStroke="#FFFFFF" stroke="#FFFFFF"/>
				<DataSeries id={dataSeriesId} indicator={RSI} options={options} >
					<RSISeries {...RSI_SERIES_PROPS} />
				</DataSeries>
			</Chart>;
			toolTip = <RSITooltip forChart={chartId} forDataSeries={dataSeriesId} key={`${chartId}-${dataSeriesId}`}
				onClick={logger.bind(null, { chartId, dataSeriesId }, options)} origin={[-48, 15]}/>;
			toolTipDY = dy;
			break;
		}
		case "FullStochasticOscillator": {
			chart = (showXAxis, dataSeries, noOfChartsAfter = 0) => <Chart id={chartId} key={chartId} yMousePointerDisplayLocation="right" yMousePointerDisplayFormat={(y) => y.toFixed(2)}
						height={chartHeight} origin={(w, h) => [0, h - chartHeight - chartHeight * noOfChartsAfter]} padding={{ top: 5, right: 0, bottom: 5, left: 0 }} >
				{ getXAxis(showXAxis, verticalGrid) }
				<YAxis axisAt="right" orient="right" ticks={2} tickStroke="#FFFFFF" stroke="#FFFFFF" />
				<DataSeries id={dataSeriesId} indicator={FullStochasticOscillator} options={options} >
					<StochasticSeries {...STOCHASTIC_SERIES_PROPS} />
				</DataSeries>
			</Chart>;
			toolTip = <StochasticTooltip forChart={chartId} forDataSeries={dataSeriesId} key={`${chartId}-${dataSeriesId}`}
				onClick={logger.bind(null, { chartId, dataSeriesId }, options)} origin={[-48, 15]}>Full STO</StochasticTooltip>;
			toolTipDY = dy;
			break;
		}
	}
	return { chart, dataSeries, toolTip, toolTipDY };
}

class ChartWidget extends PureComponent {

	componentWillUnmount() {
		// console.log("componentWillUnmount");
	}
	componentWillMount() {
		// console.log("componentWillMount");
	}
	componentDidMount() {
		// var demoComplete = Cookies.get("demoComplete") === "Y";
		var { demoComplete } = this.props;

		if (!demoComplete) {
			var { width, height } = this.props;
			var gridHeight = height - margin.bottom;

			var el = ReactDOM.findDOMNode(this.refs.canvas).parentNode;

			var tooltip = ReactDOM.findDOMNode(this.refs.MACDTooltip).innerHTML.replace(/data-reactid="[^"]*"/g, "");
			// d3.select(el).append("svg").attr("width", 400).attr("height", 20).append(svgTooltip);
			var tooltipTransform = ReactDOM.findDOMNode(this.refs.MACDTooltip).getAttribute("transform");
			var chartTransform = d3.select(el).select("svg").select("g").attr("transform");

			var chartTop = Number(chartTransform.match(/,([^)]*)/)[1].trim()) + 3;
			var tooltipTop = Number(tooltipTransform.match(/,([^)]*)/)[1].trim());

			d3.select(el)
				.append("div")
					.attr("style", `position: absolute; top: ${ tooltipTop }px; left: 15px; height: 20px; width: 400px`)
					.attr("data-intro", "Click on tooltip of any indicator to change its settings")
					.attr("data-position", "top")
					.attr("data-tooltipClass", "tooltip-intro")
					// .attr("data-step", "5")
					.attr("class", "removemeafterload")
				.append("svg")
					.attr("width", 400).attr("height", 20)
				.append("g")
					.attr("transform", `translate(${ 7.5 }, ${ chartTop })`)
				.html(tooltip);
		}
	}
	getDataInfo() {
		return this.refs.canvas.getDataInfo();
	}
	setViewRange(start, end) {
		this.refs.canvas.setViewRange(start, end);
	}
	render() {
		// console.log("ChartWidget - render()");
		var { width, height, resized, data, indicators, interactiveIndicator,
			type, transforms, exchange, security, grid } = this.props;
		var { onIndicatorSettingsChange, onChartSettingsChange, onZoom, onPan } = this.props;

		var chartSettings = transforms[transforms.length - 1].options;
		// console.log(interactiveIndicator);
		var MainChartSeries = getMainChartType(type);
		var mainChartProps = getMainChartTypeProps(type);

		var gridHeight = height - margin.top - margin.bottom;
		var gridWidth = width - margin.left - margin.right;

		var chartHeight = Math.min(gridHeight / 5, 125);

		var horizontalGrid = grid
			? { innerTickSize: -1 * gridWidth, tickStrokeOpacity: 0.1 }
			: { };

		var verticalGrid = grid
			? { innerTickSize: -1 * gridHeight, tickStrokeOpacity: 0.1 }
			: { };

		var chartId = 3, dataSeriesId = 1;
		var charts = [
			(showXAxis, dataSeries, noOfChartsAfter = 0) => (<Chart id={0} key={0} yMousePointerDisplayLocation="right"
					height={gridHeight - chartHeight * (noOfChartsAfter - 1)} yMousePointerDisplayFormat={(y) => y.toFixed(2)}>
				<YAxis axisAt="right" orient="right" ticks={5} {...horizontalGrid} tickStroke="#FFFFFF" stroke="#FFFFFF" />
				<DataSeries id={0} yAccessor={MainChartSeries.yAccessor} >
					<MainChartSeries {...mainChartProps} />
				</DataSeries>
				{dataSeries}
				<FibonacciRetracement id={0} enabled={interactiveIndicator === "Fib Retracement"} {...FIB_RETRACEMENT_PROPS} />
				<TrendLine id={1} enabled={interactiveIndicator === "TrendLine"}
						snap={true} snapTo={d => [d.open, d.high, d.low, d.close]} {...TRENDLINE_PROPS} />
			</Chart>),
			(showXAxis, dataSeries, noOfChartsAfter = 0) => (<Chart id={1} key={1} yMousePointerDisplayLocation="left" yMousePointerDisplayFormat={d3.format(".4s")}
					height={chartHeight} origin={(w, h) => [0, h - chartHeight - chartHeight * noOfChartsAfter]} >
				<YAxis axisAt="left" orient="left" ticks={5} tickFormat={d3.format("s")} tickStroke="#FFFFFF" stroke="#FFFFFF"/>
				{ getXAxis(showXAxis, verticalGrid) }
				<DataSeries id={0} yAccessor={(d) => d.volume} >
					<HistogramSeries fill={(d) => d.close > d.open ? "#6BA583" : "#db0000"} opacity={0.5} />
				</DataSeries>
			</Chart>),
		];
		var dataSeries = [];
		var toolTips = [];
		var toolTipDY = 5;

		var movingAverageTooltipExists = false;
		for (var i = 0; i < indicators.length; i++) {
			var each = indicators[i];
			var config = getConfig(each, toolTipDY, movingAverageTooltipExists, chartHeight, verticalGrid, onIndicatorSettingsChange);
			if (config.chart) {
				charts.push(config.chart);
			}
			if (config.dataSeries) {
				dataSeries.push(config.dataSeries);
			}
			if (config.toolTip) {
				var ToolTip = config.toolTip;
				var forChart = (config.chart) ? chartId - 1 : 1;
				toolTips.push(config.toolTip); // <ToolTip key={`${ chartId }-${ dataSeriesId }`} forChart={forChart} origin={[-48, toolTipDY]} />);
				toolTipDY = config.toolTipDY;
			}
			if (each.type === "EMA" || each.type === "SMA") movingAverageTooltipExists = true;
		}
		var chartsJSX = [];
		for (var i = 0; i < charts.length; i++) {
			var each;

			if (i === charts.length - 1) {
				// last
				each = charts[i](true, dataSeries, charts.length - i - 1);
			} else {
				each = charts[i](false, dataSeries, charts.length - i - 1);
			}
			chartsJSX.push(each);
		};
		// console.log(chartsJSX[2]);
		return (
			<ChartCanvas ref="canvas" width={width} height={height}
				margin={margin} initialDisplay={200}
				dataTransform={transforms}
				data={data} type="hybrid">
				{[last(chartsJSX)].concat(chartsJSX.slice(0, chartsJSX.length - 1))}
				<BackgroundText
					x={(width - margin.left - margin.right) / 2 + margin.left}
					y={(height - margin.top - margin.bottom) / 2 + margin.top}
					fontSize={0.08 * height}
					strokeOpacity={0} >
					{interval => `${ security }(NASDAQ), ${ interval }`}
				</BackgroundText>
				<MouseCoordinates xDisplayFormat={d3.time.format("%Y-%m-%d")} stroke="#FFFFFF" opacity={0.4}/>
				<EventCapture mouseMove={true} zoom={true} pan={true} mainChart={1} defaultFocus={false} onZoom={onZoom} onPan={onPan} />
				<TooltipContainer>
					<OHLCTooltip forChart={0} origin={[-48, 0]} onClick={onChartSettingsChange.bind(null, type, chartSettings)} />
					{toolTips}
				</TooltipContainer>
			</ChartCanvas>
		);
	}
}

/* ChartWidget.propTypes = {
};
*/
function last(arr) {
	if (arr && arr.length > 0) {
		return arr[arr.length - 1];
	}
	return undefined;
}
function first(arr) {
	if (arr && arr.length > 0) {
		return arr[0];
	}
	return undefined;
}

export default ChartWidget;


/** WEBPACK FOOTER **
 ** ./src/scripts/components/page/ChartPage/ChartWidget.jsx
 **/